import * as Sentry from '@sentry/react';
import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import {
  BrowserRouter as Router,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { SnackbarProvider, closeSnackbar } from 'notistack';
import { CookiesProvider } from 'react-cookie';
import { PersistGate } from 'redux-persist/integration/react';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

import AppRoot from './modules/AppRoot';
import reportWebVitals from './reportWebVitals';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

import store, { persistor } from './modules/state-mgmt/store';

import './index.css';
import './assets/styles/rich-text.css';
import './i18n';
import { AuthService } from 'services/AuthService';

Sentry.init({
  dsn: 'https://c3e464957deffc25408500199e07f616@o4504752820191232.ingest.us.sentry.io/4511018958454785',
  environment: process.env.REACT_APP_ENV || 'local',
  enabled: process.env.REACT_APP_ENV === 'prd', // To test locally, change to: enabled: true
  sendDefaultPii: true,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: 0.2,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

AuthService.configure();

function CloseButton({ snackbarKey }) {
  return (
    <IconButton size="small" onClick={() => closeSnackbar(snackbarKey)} color="inherit">
      <CloseIcon fontSize="small" />
    </IconButton>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root-worker-portal') as HTMLElement);
root.render(
  // <React.StrictMode>
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <SnackbarProvider
        maxSnack={5}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        action={snackbarKey => <CloseButton snackbarKey={snackbarKey} />}
      />
      <CookiesProvider>
        <Router>
          <AppRoot />
        </Router>
      </CookiesProvider>
    </PersistGate>
  </Provider>,
  // </React.StrictMode>
);

// Register service worker for offline support
serviceWorkerRegistration.register({
  onSuccess: () => {
    // First-ever SW install: the SW is installing but clientsClaim() hasn't fired yet.
    // Listen for controllerchange — when the SW activates and claims this client,
    // reload so the SW intercepts all requests and caches main.js for offline use.
    navigator.serviceWorker?.addEventListener('controllerchange', () => {
      console.log('Worker Portal - First SW install. Reloading to populate runtime cache...');
      window.location.reload();
    });
  },
  onUpdate: registration => {
    // Store the registration globally so CacheBuster can message the waiting SW
    (window as any).__SW_REGISTRATION__ = registration;
  },
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
